import { useState } from 'react';
import { Info, FileSpreadsheet } from 'lucide-react';
import type { AvailabilityTimeEncoding } from '@ooh/contracts';
import { IconButtonWithTooltip } from '@ooh/ui/components/icon-button-with-tooltip';
import { ModalDialog } from '@ooh/ui/components/modal-dialog';
import { ExampleFileSnippet } from '@ooh/ui/components/example-file-snippet';

export interface TimeEncodingHelpDialogSection {
  encoding: AvailabilityTimeEncoding;
  title: string;
  explanation: string;
  /**
   * File name shown as a small "file card" above the example table — only
   * set for the `global` encoding, which has no date columns to preview, so
   * the filename itself is shown carrying the period instead.
   */
  exampleFileName?: string;
  example: {
    headers: string[];
    rows: string[][];
  };
}

export interface TimeEncodingHelpDialogLabels {
  /** Tooltip text + accessible name for the (i) trigger button. */
  triggerLabel: string;
  dialogTitle: string;
  sections: TimeEncodingHelpDialogSection[];
}

export interface TimeEncodingHelpDialogProps {
  labels: TimeEncodingHelpDialogLabels;
  /** Only sections whose `encoding` is in this list are shown — mirrors
   * `TimeEncodingSelector`'s own `encodingOptions` so the help dialog never
   * explains an option the operator isn't actually offered. */
  encodingOptions: readonly AvailabilityTimeEncoding[];
  testIdPrefix?: string;
}

/**
 * (i) trigger + dialog explaining each currently-offered
 * `AvailabilityTimeEncoding` option: a short explanation plus a small,
 * non-interactive "what such a file looks like" example built from
 * `@ooh/ui`'s `ExampleFileSnippet`. Self-contained (owns its own open state)
 * so any `TimeEncodingSelector` caller renders it via `labelSlot` with no
 * extra wiring.
 */
export function TimeEncodingHelpDialog({
  labels,
  encodingOptions,
  testIdPrefix = 'time-encoding-help',
}: TimeEncodingHelpDialogProps) {
  const [open, setOpen] = useState(false);
  const sections = labels.sections.filter((section) => encodingOptions.includes(section.encoding));

  if (sections.length === 0) return null;

  return (
    <>
      <IconButtonWithTooltip
        icon={Info}
        tooltip={labels.triggerLabel}
        variant="ghost"
        size="icon-sm"
        onClick={() => setOpen(true)}
        testId={`${testIdPrefix}-trigger`}
      />
      {open ? (
        <ModalDialog
          header={
            <h2 className="text-[15px] font-semibold text-foreground">{labels.dialogTitle}</h2>
          }
          onClose={() => setOpen(false)}
          maxWidth="max-w-2xl" // eslint-disable-line i18next/no-literal-string -- Tailwind class, not user-facing copy
          testId={`${testIdPrefix}-dialog`}
        >
          <div className="space-y-6">
            {sections.map((section) => (
              <div key={section.encoding} className="space-y-2">
                <p className="text-[13px] font-semibold text-foreground">{section.title}</p>
                <p className="text-xs text-muted-foreground">{section.explanation}</p>
                {section.exampleFileName ? (
                  <div className="flex w-fit items-center gap-2 rounded-md border border-border/60 bg-muted/30 px-3 py-2">
                    <FileSpreadsheet
                      className="h-4 w-4 shrink-0 text-muted-foreground"
                      strokeWidth={1.8}
                    />
                    <span className="text-xs font-medium text-foreground">
                      {section.exampleFileName}
                    </span>
                  </div>
                ) : null}
                <ExampleFileSnippet
                  headers={section.example.headers}
                  rows={section.example.rows}
                  testId={`${testIdPrefix}-example-${section.encoding}`}
                />
              </div>
            ))}
          </div>
        </ModalDialog>
      ) : null}
    </>
  );
}
